// Event-status colour tokens for the Spatial view.
//
// These stay in JS on purpose — they are not decoration, they are DATA: the
// timeline reads them to size and colour a per-event risk bar
// (`width: ${score}%`), to tint sub-event rows from a group's status, and to
// colour a score. CSS cannot compute those from a status string, so the values
// have to be reachable from the component.
//
// Design mapping (Tailwind palette, verified against the design DOM):
//   {colour}-500 -> fills, badges, borders, bar fill      -> STATUS_COLOR
//   {colour}-600 -> the darker variant used for scores    -> STATUS_ICON_COLOR
//
// NB the node circle uses ONE token three ways (fill @12%, border @40%, glyph at
// full strength) — see .node-circle. `hsl(var(--destructive))` in the design
// resolves to #ef4444, i.e. STATUS_COLOR.failed.
//
// Extracted from styled.js so that file can be deleted once every component is
// on SCSS modules.

export const STATUS_COLOR = {
  success: '#22c55e', // green-500
  neutral: '#f59e0b', // amber-500
  failed: '#ef4444' // red-500
};

export const STATUS_ICON_COLOR = {
  success: '#16a34a', // green-600
  neutral: '#d97706', // amber-600
  failed: '#dc2626' // red-600
};

// Hex alpha suffixes for the washes the design applies to these same tokens.
// SUB-EVENT surfaces only. Verified against the design's DOM (2026-08-13/14):
// a sub-event row is `bg-{status}/[0.03]` and its icon is
// `bg-{status}/12 border-{status}/40`.
//
// The TIMELINE NODE CIRCLE is a DIFFERENT pair — `/10` fill and `/30` border when
// plain, `/15` and `/50` when triggered (see EventTimeline/index.scss). Do not
// reuse these three values there: assuming one shared pair is exactly how the node
// circle ended up at 12%/40% and had to be corrected back.
export const STATUS_ALPHA = {
  row: '08', //  3% — bg-{status}/[0.03] on a sub-event row
  fill: '1f', // 12% — bg-{status}/12 on a sub-event icon
  border: '66' // 40% — border-{status}/40 on a sub-event icon
};

// ── SCORE BANDS (design update, 2026-08-14) ─────────────────────────────────
// The updated design RE-SEMANTICISES colour on this surface. Its legend reads
//   High (70+) · Medium (40–69) · Low (<40)
// where it previously read Failed/Flagged · Neutral · Success. So a hue no longer
// says what HAPPENED to an event, it says how RISKY the event scored.
//
// Consequences, deliberately:
//   - the node circle carries no hue at all any more (it is neutral grey)
//   - the score badge is the only thing on a node that is coloured, and its hue
//     comes from the band below, not from `event.status`
//   - severity does NOT disappear — it survives as the text pill in the detail
//     panel header ("failed"), which is where the updated design puts it
//
// Thresholds are the ones the product already states in its own explanation copy
// ("Thresholds: Low 0-39 · Medium 40-69 · High 70-100", alertDetailMock.js), and
// they agree with the design's legend.
export const SCORE_BAND = { HIGH: 'high', MEDIUM: 'medium', LOW: 'low' };

// The three hues are UNCHANGED — the design reuses the same red/amber/green, it
// just attaches a different meaning to them. Aliased rather than re-declared so a
// palette correction to STATUS_COLOR cannot leave the bands behind.
export const SCORE_BAND_COLOR = {
  [SCORE_BAND.HIGH]: STATUS_COLOR.failed, //   70+   red-500
  [SCORE_BAND.MEDIUM]: STATUS_COLOR.neutral, // 40-69 amber-500
  [SCORE_BAND.LOW]: STATUS_COLOR.success //    <40   green-500
};

/**
 * The product's own default cut-offs (FIN-13569).
 *
 * WAS 40 / 70, HARDCODED, matching nothing. Those came from the design's legend
 * and from explanation copy in a mock; the tenant's real bands live in the
 * scoring configuration, whose shipped defaults are these — see
 * `configureInstanceDashboard/AlertScoringTab/defaults.js`. QA's tenant is
 * configured 33 / 66, so a score of 68 was badged Medium here while the
 * configuration placed it in the High band.
 *
 * Used only when the configuration has not arrived (or the deployment has none),
 * so the timeline still bands scores rather than rendering them all one colour.
 */
export const DEFAULT_SCORE_BANDS = { lowMin: 0, lowMax: 33, mediumMax: 66 };

/**
 * Band for a risk score, against the tenant's configured cut-offs.
 *
 * BOUNDARY RULE FROM THE SETTINGS SCREEN'S OWN COPY — "Low ≤ lowMax · Good ≤
 * mediumMax · High > mediumMax" (AlertScoringTab/index.js:67). So on 33/66 a
 * score of exactly 33 is Low and exactly 66 is Good, which is what the slider
 * labels `0 – 33` and `33 – 66` mean. Getting this off by one would put a
 * boundary score in a band the settings screen says it is not in.
 *
 * Returns undefined for a missing or non-numeric score — an unscored event must
 * render NO badge rather than a colourless one, which is what the updated design
 * shows on its "Login Attempt" node.
 *
 * @param {number} score
 * @param {{lowMax:number, mediumMax:number}} [bands]
 * @returns {'high'|'medium'|'low'|undefined}
 */
export const scoreBand = (score, bands) => {
  if (typeof score !== 'number' || Number.isNaN(score)) return undefined;

  const { lowMax, mediumMax } = bands || DEFAULT_SCORE_BANDS;
  if (score > mediumMax) return SCORE_BAND.HIGH;
  if (score > lowMax) return SCORE_BAND.MEDIUM;
  return SCORE_BAND.LOW;
};

export default STATUS_COLOR;
